import { useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import Validations from "@src/utility/helpers/validations";
import { object } from "yup";
import useAxios from "@hooks/use-axios";
import useSearchParams from "@hooks/use-search-params";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
    name : "",
    itcat: ""
};

const usePlatforms = () => {
    const { axiosInstance, loading, cancelAllRequests } = useAxios({
        base         : "panel",
        initialLoader: true
    });
    const { axiosInstance: upsertAxiosInstance, loading: updateLoading } =
        useAxios({ base: "panel" });
    const { axiosInstance: productKeyAxiosInstance, loading: productKeyLoading } =
        useAxios({ base: "panel" });

    const { getSearchParams } = useSearchParams();
    const [data, setData] = useState({ platforms: [], pagination: {} });
    const [queryParams, setQueryParams] = useState({});
    const [modalConfig, setModalConfig] = useState({ isOpen: false, row: {} });
    const [productKeyModal, setProductKeyModal] = useState({ 
        isOpen      : false, 
        row         : {}, 
        data        : null, 
        regenerating: false 
    });

    const { validateIfPresent, requiredValidation } = Validations();
    const validationSchema = object().shape({
        name : requiredValidation({ name: "Name" }),
        itcat: validateIfPresent({
            name: "Intercom Access Token",
            min : 60
        })      
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema)
    });

    const { control, handleSubmit, reset } = methods;
    const { isDirty } = useFormState({ control });

    const handleModalConfig = (row = defaultValues) => {
        setModalConfig(prev => ({ row, isOpen: !prev.isOpen }));
        reset(row);
    };

    const handleProductKeyModalConfig = (row = {}) => {
        setProductKeyModal(prev => ({ 
            ...prev, 
            row, 
            isOpen: !prev.isOpen, 
            data  : null 
        }));
    };

    const getPlatforms = async () => {
        const { data, pagination } = await axiosInstance.get("/platform", {
            params: {
                page : getSearchParams("page"),
                limit: getSearchParams("limit")
            }
        });
        setData({
            platforms : data || [],
            pagination: pagination || {}
        });
    };
    
    const getProductkey = async (platformId) => {
        const { data } = await productKeyAxiosInstance.get("/platform/product-key", {
            params: {
                platform_id: platformId
            }
        });
        return data;
    };

    const regenerateProductKey = async (platformId) => {
        const { data } = await productKeyAxiosInstance.post(
            "/platform/product-key",
            {}, 
            {
                params: {
                    platform_id: platformId
                }
            }
        );
        return data;
    };

    const handleSettingsClick = async (product, e) => {
        e.stopPropagation();
        handleProductKeyModalConfig(product);    
        const data = await getProductkey(product._id);
        setProductKeyModal(prev => ({ 
            ...prev, 
            data 
        }));  
    };
    const handleRegenerateProductKey = async () => {
        if (!productKeyModal.row._id) return;

        setProductKeyModal(prev => ({ ...prev, regenerating: true }));

        const data = await regenerateProductKey(productKeyModal.row._id);

        setProductKeyModal(prev => ({
            ...prev,
            data,
            regenerating: false
        }));
    };

    const handleRowActions = async (row = defaultValues, action) => {
        if (action === "delete") {
            await axiosInstance.delete(`/platform/${row._id}`);
            getPlatforms();
            return;
        }
        handleModalConfig(row);
    };

    useEffect(() => {
        let isActive = true;
        if (isActive) {
            getPlatforms();
        }
        return () => {
            isActive = false;
            cancelAllRequests();
        };
    }, [queryParams]);

    const onSubmit = () => {
        handleSubmit(async data => {
            const payload = {
                name  : data.name,
                status: "active",
                itcat : data.itcat
            };
            if (!modalConfig.row._id) {
                await upsertAxiosInstance.post("/platform", payload);
            } else {
                await upsertAxiosInstance.patch(
                    `/platform/${modalConfig.row._id}`,
                    payload
                );
            }
            getPlatforms();
            handleModalConfig();
        })();
    };

    return {
        data,
        loading,
        getPlatforms,
        setQueryParams,
        handleModalConfig,
        handleRowActions,
        methods,
        modalConfig,
        onSubmit,
        updateLoading,
        isOpen: modalConfig.isOpen,
        isDirty,
        productKeyModal,
        handleProductKeyModalConfig,
        handleSettingsClick,
        handleRegenerateProductKey,
        productKeyLoading
    };
};

export default usePlatforms;